import React, { useCallback, useEffect, useRef } from "react";

import { m } from "motion/react";

import { AgentPlaygroundContent } from "src/agents/AgentVersionPage/AgentPlayground";
import { usePlaygroundContext } from "src/agents/AgentVersionPage/PlaygroundContext";
import { usePlaygroundBackgroundToast } from "src/agents/AgentVersionPage/usePlaygroundBackgroundToast";
import {
  SideDrawer,
  useSideDrawer,
  useSideDrawerCoordinator,
} from "src/agents/base/SideDrawer";
import { AgentSessionActionsMenu } from "src/base-components/agents/AgentSessionActionsMenu";

type AgentPlaygroundDrawerProps = {
  agentId: string;
  versionId: string;
};

export const AgentPlaygroundDrawer: React.FC<AgentPlaygroundDrawerProps> = ({
  agentId,
  versionId,
}) => {
  const { sessionState, rawSessionData, ping } = usePlaygroundContext();

  const currentSessionId =
    sessionState.status !== "idle" ? sessionState.sessionId : null;

  // Ping when drawer opens (detect via isOpen from useSideDrawer)
  const { isOpen } = useSideDrawer("playground");
  const prevIsOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpenRef.current) {
      ping(); // Uses current (potentially fresh) session ID
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, ping]);

  // Background toast when drawer is closed during an active session
  const { requestOpen } = useSideDrawerCoordinator();
  const handleOpenPlayground = useCallback(() => {
    requestOpen({ type: "playground" });
  }, [requestOpen]);

  usePlaygroundBackgroundToast({
    sessionState,
    isDrawerOpen: isOpen,
    onOpenDrawer: handleOpenPlayground,
  });

  // Show extra actions when there's an active session (booting is just pre-warming, not a session)
  const hasActiveSession =
    sessionState.status !== "idle" &&
    !(sessionState.status === "active" && sessionState.substatus === "booting");
  const extraActions = hasActiveSession ? (
    <m.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.15, ease: "easeIn" }}
    >
      <AgentSessionActionsMenu
        backendStatus={rawSessionData?.status}
        dataLoc="playground-session-actions-menu"
        rawSessionData={rawSessionData ?? null}
        sessionId={currentSessionId}
      />
    </m.div>
  ) : undefined;

  return (
    <SideDrawer
      dataLoc="agent-playground-drawer"
      drawerId="playground"
      header={{
        title: "Test Agent",
        extraActions,
        closeDataLoc: "close-test-panel",
      }}
    >
      <AgentPlaygroundContent agentId={agentId} versionId={versionId} />
    </SideDrawer>
  );
};
